import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { formatDuration, intervalToDuration } from "date-fns";
import Input from "components/Input/Input";
import ColorInput from "components/Input/ColorInput";
import Button from "components/Button/Regular";
import Select from "components/Input/Select";
import { ReactComponent as PatientFallbackIcon } from "assets/images/patient-fallback.svg";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { readAnimal, updateOrgAnimal, uploadAnimalImage } from "services/patients.service";
import vgToast from "utils/toast";
import {
  PATIENT_ACTIVATION_STATE,
  PATIENT_BLOOD_GROUP,
  PATIENT_DECEASED_STATUS,
  PATIENT_DESEXED_STATUS,
  PATIENT_GENDER,
  PATIENT_SPECIES,
  patientActivationOptions,
  patientBloodGroupOptions,
  patientBreedOptions,
  patientDeceasedOptions,
  patientDesexedOptions,
  patientGenderOptions,
  patientSpeciesOptions,
  toSelectOptions,
} from "constants/patientEnums";

type FormValues = {
  patientsName: string;
  species: string;
  animalBreed: string;
  active: string;
  dateOfBirth: string;
  deceasedStatus: string;
  deceasedDate: string;
  mainColour: string;
  secondaryColour: string;
  desexedStatus: string;
  microchipNumber: string;
  insuranceRefNumber: string;
  passportNumber: string;
  sex: string;
  bloodGroup: string;
};

const normalizeHexColor = (value: string | undefined, fallback = "#000000") => {
  if (!value) return fallback;
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
};

// Backend uses 1970-01-01 (Unix epoch) as a sentinel for "no value" so it can pass
// the Is_Valid_DateTime range gate. Treat it as empty in the UI so the date input
// shows the dd-mm-yyyy placeholder and the age widget renders N/A.
const EPOCH_SENTINEL_DATE = "1970-01-01";
const formatDateForInput = (value: string | undefined) => {
  const trimmed = value?.slice(0, 10) ?? "";
  return trimmed === EPOCH_SENTINEL_DATE ? "" : trimmed;
};

const toApiDate = (value: string) => (value ? `${value}T00:00:00.000Z` : undefined);

const UpdatePatientForm = () => {
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently } = useAuth0();
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [patientImageUrl, setPatientImageUrl] = React.useState<string | null>(null);
  const [imageBroken, setImageBroken] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const animalId = id ? Number(id) : NaN;

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      mainColour: "#000000",
      secondaryColour: "#ffffff",
      dateOfBirth: "",
      deceasedDate: "",
    },
  });

  const getAgeStringFromDate = (dateValue: string | undefined) => {
    if (!dateValue) return "N/A";
    const date = new Date(`${dateValue}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime())) return "N/A";

    const interval = { start: date, end: Date.now() };
    const duration = intervalToDuration(interval);

    const formatted = formatDuration({
      years: duration.years,
      months: duration.months,
      days: duration.days,
    });

    return formatted || "0 days";
  };

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id || !Number.isFinite(animalId) || animalId <= 0 || orgId <= 0) return;
      setLoadError(null);
      try {
        const token = await getAccessTokenSilently();
        const { data, error } = await readAnimal(token, orgId, animalId);
        if (cancelled) return;
        if (error) {
          setLoadError(error.message);
          return;
        }
        if (!data) return;
        const url = data.cloudFileUrl?.trim();
        setPatientImageUrl(url && url.length > 0 ? url : null);
        setImageBroken(false);
        reset({
          patientsName: data.name,
          species: String(data.animalSpecies ?? PATIENT_SPECIES.CANINE),
          animalBreed: String(data.animalBreed ?? 1),
          active: String(data.isActive ?? PATIENT_ACTIVATION_STATE.ACTIVE),
          dateOfBirth: formatDateForInput(data.dateOfBirth),
          deceasedStatus: String(data.deceasedStatus ?? PATIENT_DECEASED_STATUS.ALIVE),
          deceasedDate: formatDateForInput(data.deceasedDate),
          mainColour: normalizeHexColor(data.mainColour),
          secondaryColour: normalizeHexColor(data.secondaryColour, "#ffffff"),
          desexedStatus: String(
            data.desexedStatus ?? PATIENT_DESEXED_STATUS.NOT_APPLICABLE
          ),
          microchipNumber: data.microchipId ?? "",
          insuranceRefNumber: data.insuranceReferenceNumber ?? "",
          passportNumber: data.passportNumber ?? "",
          sex: String(data.gender ?? PATIENT_GENDER.MALE),
          bloodGroup: String(data.bloodGroup ?? PATIENT_BLOOD_GROUP.NOT_APPLICABLE),
        });
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, animalId, orgId, getAccessTokenSilently, reset]);

  const handleImageClick = () => {
    if (uploadingImage) return;
    if (orgId <= 0 || !Number.isFinite(animalId) || animalId <= 0) {
      vgToast.error("Save the patient before uploading a photo.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (orgId <= 0 || !Number.isFinite(animalId) || animalId <= 0) {
      vgToast.error("Missing organisation or patient id.");
      return;
    }
    setUploadingImage(true);
    const previewUrl = URL.createObjectURL(file);
    const previousUrl = patientImageUrl;
    setImageBroken(false);
    setPatientImageUrl(previewUrl);
    try {
      const token = await getAccessTokenSilently();
      const { data, error } = await uploadAnimalImage(token, orgId, animalId, file);
      if (error) {
        setPatientImageUrl(previousUrl);
        vgToast.error(error.message);
        return;
      }
      if (data?.cloudFileUrl) {
        setPatientImageUrl(data.cloudFileUrl);
      }
      vgToast.success("Photo uploaded.");
    } catch (err) {
      setPatientImageUrl(previousUrl);
      vgToast.error((err as Error).message);
    } finally {
      URL.revokeObjectURL(previewUrl);
      setUploadingImage(false);
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (orgId <= 0 || !Number.isFinite(animalId) || animalId <= 0) {
      vgToast.error("Missing organisation or patient id.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const species = Number(data.species) || PATIENT_SPECIES.CANINE;
      const animalBreed = Number(data.animalBreed) || 2; // Other (1=Unknown is rejected by validator)
      const gender = Number(data.sex) || PATIENT_GENDER.MALE;
      const blood = Number(data.bloodGroup) || PATIENT_BLOOD_GROUP.NOT_APPLICABLE;
      const isActive = Number(data.active) || PATIENT_ACTIVATION_STATE.ACTIVE;
      const deceasedStatus =
        Number(data.deceasedStatus) || PATIENT_DECEASED_STATUS.ALIVE;
      const desexedStatus =
        Number(data.desexedStatus) || PATIENT_DESEXED_STATUS.NOT_APPLICABLE;
      const dob = toApiDate(data.dateOfBirth);
      const dec = toApiDate(data.deceasedDate);

      const { error } = await updateOrgAnimal(token, {
        orgId,
        animalId,
        name: data.patientsName,
        animalSpecies: species,
        animalBreed,
        dateOfBirth: dob,
        gender,
        bloodGroup: blood,
        microchipId: data.microchipNumber,
        insuranceReferenceNumber: data.insuranceRefNumber,
        passportNumber: data.passportNumber,
        isActive,
        deceasedStatus,
        deceasedDate: dec,
        mainColour: data.mainColour,
        secondaryColour: data.secondaryColour,
        desexedStatus,
      });
      if (error) vgToast.error(error.message);
      else vgToast.success("Patient saved.");
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-10">
      {loadError && (
        <div className="mb-4 text-sm text-red-500">{loadError}</div>
      )}
      <div className="grid grid-cols-1 gap-6 mb-10 sm:grid-cols-2 sm:gap-10 lg:grid-cols-3">
        <button
          type="button"
          onClick={handleImageClick}
          disabled={uploadingImage}
          aria-label="Upload patient photo"
          className="row-span-2 justify-self-center relative group h-44 w-44 shrink-0 rounded-full border border-vg-blue/20 bg-vg-blue/[0.04] overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-vg-blue/40 focus-visible:ring-offset-2"
        >
          {patientImageUrl && !imageBroken ? (
            <img
              src={patientImageUrl}
              alt=""
              onError={() => setImageBroken(true)}
              className="h-full w-full object-cover transition group-hover:opacity-80"
            />
          ) : (
            <PatientFallbackIcon className="absolute inset-0 m-auto h-24 w-24 text-vg-blue/40" />
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-white text-xs font-semibold tracking-wide opacity-0 group-hover:opacity-100 transition pointer-events-none">
            {uploadingImage ? "UPLOADING…" : "CHANGE PHOTO"}
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageChange}
        />

        <Input
          {...register("patientsName")}
          label="Patients Name"
          placeholder="Enter first name"
        />

        <Select
          {...register("species")}
          label="Species"
          options={toSelectOptions(patientSpeciesOptions)}
        />

        <Select
          {...register("animalBreed")}
          label="Breed"
          options={toSelectOptions(patientBreedOptions)}
        />

        <Input
          type="date"
          {...register("dateOfBirth")}
          label="Date of Birth"
          placeholder="Enter Date of Birth"
        />

        <Select
          {...register("active")}
          label="Activation Status"
          options={toSelectOptions(patientActivationOptions)}
        />

        <Input
          type="date"
          {...register("deceasedDate")}
          label="Deceased Date"
          placeholder="Enter Date of Decease"
        />

        <ColorInput
          label="Main Colour"
          value={watch("mainColour")}
          onChange={(value) => setValue("mainColour", value, { shouldDirty: true })}
        />

        <div className="flex flex-col gap-y-2 grow">
          <label className="text-gray-400 text-sm">Age</label>
          <p className="border border-gray-300 placeholder:text-sm p-2 min-w-0">
            {getAgeStringFromDate(watch("dateOfBirth"))}
          </p>
        </div>

        <Select
          {...register("deceasedStatus")}
          label="Deceased Status"
          options={toSelectOptions(patientDeceasedOptions)}
        />

        <ColorInput
          label="Secondary Colour"
          value={watch("secondaryColour")}
          onChange={(value) =>
            setValue("secondaryColour", value, { shouldDirty: true })
          }
        />

        <Input
          {...register("passportNumber")}
          label="Passport Number"
          placeholder="Enter Insurance Ref Number"
        />

        <Input
          {...register("microchipNumber")}
          label="Microchip Number"
          placeholder="Enter Microchip Number"
        />

        <Input
          {...register("insuranceRefNumber")}
          label="Insurance Ref Number"
          placeholder="Enter Insurance Ref Number"
        />

        <Select
          {...register("sex")}
          label="Sex"
          options={toSelectOptions(patientGenderOptions)}
        />

        <Select
          {...register("desexedStatus")}
          label="Desexed Status"
          options={toSelectOptions(patientDesexedOptions)}
        />

        <Select
          {...register("bloodGroup")}
          label="Blood Group"
          options={toSelectOptions(patientBloodGroupOptions)}
        />
      </div>

      <div className="flex justify-end gap-4 flex-wrap">
        <Button color="Primary" type="submit" disabled={submitting}>
          <span>{submitting ? "Saving…" : "Update Patient"}</span>
        </Button>
      </div>
    </form>
  );
};

export default UpdatePatientForm;
